import React, { useMemo } from "react";
import { Row, Col } from "react-bootstrap";

import { FORM_FIELD_KEYS } from "../../../../constants/forms";
import { getFieldProps } from "../../../../utils/form-fields";

import TextInput from "../../../controls/TextInput";
import Checkbox from "../../../controls/Checkbox";
import Select from "../../../controls/Select";
import styled from "styled-components";
import { isStateLimited, state_array as STATES } from "../../../../../variables/Variables";

const StyledStateDesc = styled.div`
  font-family: 'LatoRegular';
  font-style: italic;
  font-size: 12px;
  margin-bottom: 16px;
  margin-top: -10px;
  color: rgba(27, 56, 88, 0.7);
`;

function BusinessAddressBlock({ prefix = "corp", values = {}, onChange }) {
  const showAltAddress = values[FORM_FIELD_KEYS.altAddress] === "true";

  const title = useMemo(() => {
    let titleText = "Business Address (PO Boxes are not authorized)";

    if (values.code === "EIN_Estate") {
      titleText = "Executor/Legal Personal Representative Address (PO Boxes are not authorized)";
    }
    if (values.code === "EIN_Trust") {
      titleText = "Trustee Address (PO Boxes are not authorized)";
    }

    return titleText;
  }, [values]);

  const addressHint = useMemo(() => {
    let firstPart = "Please enter the physical address of the business/entity.";
    let secondPart = "Business AddressNo P.O. Boxes Allowed";

    if (values.code === "EIN_Estate") {
      firstPart = "Enter the street address of the person that will manage the Estate.";
    }
    if (values.code === "EIN_Trust") {
      firstPart = "Please enter the physical address of the Trustee.";
    }
    if (values.code === "EIN_Church") {
      firstPart = "Please enter the physical address of the Church.";
    }
    if (values.code === "EIN_NonProfit") {
      firstPart = "Please enter the physical address of the Non Profit.";
    }

    return `${firstPart} ${secondPart}`;
  }, [values]);

  const countyHint = useMemo(() => {
    let firstPart = "Enter the county of the business/entity.";
    let secondPart = "Do not confuse “county” with “country”.";

    if (values.code === "EIN_Estate") {
      firstPart = "Enter the county of the person that will manage the Estate.";
    }
    if (values.code === "EIN_Trust") {
      firstPart = "Enter the county of the Trustee.";
    }
    if (values.code === "EIN_Church") {
      firstPart = "Enter the county of the Church.";
    }
    if (values.code === "EIN_NonProfit") {
      firstPart = "Enter the county of the Non Profit.";
    }

    return `${firstPart} ${secondPart}`;
  }, [values]);

  return (
    <Row>
      <Col xs={12}>
        <div className="inputs-block-title">
          <span>{title}</span>
        </div>
      </Col>
      <Col xs={12} md={6}>
        <TextInput
          {...getFieldProps(FORM_FIELD_KEYS.baseAddressAddress1.replace("base", prefix))}
          isCorpAddress
          hint={addressHint}
          pattern={`(?!.*(?:[pP][oO] [bB][oO][xX]))^[ a-zA-Z0-9\\\\\\/\\-]+$`}
          defaultInvalidMessage="The only special characters allowed in the Street field are - and /. PO Boxes are not acceptable."
          value={values[FORM_FIELD_KEYS.baseAddressAddress1.replace("base", prefix)]}
          onChange={onChange}
        />
      </Col>
      <Col xs={12} md={6}>
        <TextInput
          {...getFieldProps(FORM_FIELD_KEYS.baseAddressAddress2.replace("base", prefix))}
          isCorpAddress
          pattern={`(?!.*(?:[pP][oO] [bB][oO][xX]))^[ a-zA-Z0-9\\\\\\/\\-]+$`}
          defaultInvalidMessage="The only special characters allowed in the Street field are - and /. PO Boxes are not acceptable."
          value={values[FORM_FIELD_KEYS.baseAddressAddress2.replace("base", prefix)]}
          onChange={onChange}
        />
      </Col>
      <Col xs={12} md={6}>
        <TextInput
          {...getFieldProps(FORM_FIELD_KEYS.baseAddressCity.replace("base", prefix))}
          value={values[FORM_FIELD_KEYS.baseAddressCity.replace("base", prefix)]}
          onChange={onChange}
        />
      </Col>
      <Col xs={12} md={6}>
        <TextInput
          {...getFieldProps(FORM_FIELD_KEYS.baseAddressZip.replace("base", prefix))}
          value={values[FORM_FIELD_KEYS.baseAddressZip.replace("base", prefix)]}
          onChange={onChange}
        />
      </Col>
      <Col xs={12} md={6}>
        {values.code==="EIN_LLC" && isStateLimited(values?.organizedState) ? (
          <>
            <Select
              {...getFieldProps(FORM_FIELD_KEYS.baseAddressState.replace("base", prefix))}
              value={values[FORM_FIELD_KEYS.baseAddressState.replace("base", prefix)]}
              validated={values.validated}
              onChange={onChange}
              disabled
              noBottomSpace
            />
            <StyledStateDesc>
              For {STATES[values?.organizedState] || 'this state'} filings, the
              physical address must be located within the state.
            </StyledStateDesc>
          </>) : (
        <Select
          {...getFieldProps(FORM_FIELD_KEYS.baseAddressState.replace("base", prefix))}
          value={values[FORM_FIELD_KEYS.baseAddressState.replace("base", prefix)]}
          validated={values.validated}
          onChange={onChange}
        />)}
      </Col>
      <Col xs={12} md={6}>
        <TextInput
          {...getFieldProps(FORM_FIELD_KEYS.baseAddressCounty.replace("base", prefix))}
          hint={countyHint}
          value={values[FORM_FIELD_KEYS.baseAddressCounty.replace("base", prefix)]}
          onChange={onChange}
        />
      </Col>
      <Col xs={12}>
        <Checkbox
          {...getFieldProps(FORM_FIELD_KEYS.altAddress.replace("base", prefix))}
          value={values[FORM_FIELD_KEYS.altAddress.replace("base", prefix)]}
          onChange={onChange}
        />
      </Col>

      {showAltAddress && (
        <>
          <Col xs={12} md={6}>
            <TextInput
              {...getFieldProps(FORM_FIELD_KEYS.altAddressAddress1)}
              pattern="^[ a-zA-Z0-9\\/-]+$"
              defaultInvalidMessage="The only special characters allowed in the Street field are - and /"
              value={values[FORM_FIELD_KEYS.altAddressAddress1]}
              onChange={onChange}
            />
          </Col>
          <Col xs={12} md={6}>
            <TextInput
              {...getFieldProps(FORM_FIELD_KEYS.altAddressAddress2)}
              pattern="^[ a-zA-Z0-9\\/-]+$"
              defaultInvalidMessage="The only special characters allowed in the Street field are - and /"
              value={values[FORM_FIELD_KEYS.altAddressAddress2]}
              onChange={onChange}
            />
          </Col>
          <Col xs={12} md={6}>
            <TextInput
              {...getFieldProps(FORM_FIELD_KEYS.altAddressCity)}
              value={values[FORM_FIELD_KEYS.altAddressCity]}
              onChange={onChange}
            />
          </Col>
          <Col xs={12} md={6}>
            <TextInput
              {...getFieldProps(FORM_FIELD_KEYS.altAddressZip)}
              value={values[FORM_FIELD_KEYS.altAddressZip]}
              onChange={onChange}
            />
          </Col>
          <Col xs={12} md={6}>
            <Select
              {...getFieldProps(FORM_FIELD_KEYS.altAddressState)}
              value={values[FORM_FIELD_KEYS.altAddressState]}
              validated={values.validated}
              onChange={onChange}
            />
          </Col>
          <Col xs={12} md={6}>
            <TextInput
              {...getFieldProps(FORM_FIELD_KEYS.altAddressCounty)}
              hint={countyHint}
              value={values[FORM_FIELD_KEYS.altAddressCounty]}
              onChange={onChange}
            />
          </Col>
        </>
      )}

      <Col xs={12}>
        <div className="inputs-block-divider" />
      </Col>
    </Row>
  );
}

export default BusinessAddressBlock;
